import produce from 'immer';
import {useCallback, useState} from 'react';

import {TagSchema} from 'constants/RegistryConstants';
import {SchemaTag, Tag} from 'folder-items/FileConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaValidationErrorRecord} from 'schemas/SchemaValidationConstants';
import {getInitialTag, relevantKeyFromFieldType, isNullValue} from 'schemas/tags/TagUtil';

import BenchlingConfig from '../BenchlingConfig';
import {BaseTagTableProps} from './TagTable';

export function getRequiredValueErrorFromField(field: SchemaField): SchemaValidationErrorRecord {
  return {
    errorLabel: 'Required value',
    message: `Value is missing for required field "${field.name}"`,
    field,
  };
}

export interface TagTableErrorsByFieldId {
  [fieldId: number]: Array<SchemaValidationErrorRecord>;
}
export function calcTagTableErrorsByFieldID(
  tagSchema: TagSchema,
  schemaTags: Array<SchemaTag>,
  includeRequiredErrors: boolean = true
): TagTableErrorsByFieldId {
  return tagSchema.fields.reduce((errors: BaseTagTableProps['errorsByFieldId'], field) => {
    const schemaTag = schemaTags.find((schemaTag) => schemaTag.name === field.name);
    const relevantKey = (relevantKeyFromFieldType(field.fieldType) as keyof SchemaTag) || null;
    if (
      field.isRequired &&
      !(BenchlingConfig.ELN_INTEGRATION_FIELD_NOT_REQUIRED_ON_ENTRY_CREATE_MODAL && field.isIntegration) &&
      relevantKey &&
      schemaTag &&
      includeRequiredErrors &&
      isNullValue(schemaTag[relevantKey])
    ) {
      errors[field.id] = [getRequiredValueErrorFromField(field)];
    }
    return errors;
  }, {});
}

/**
 * Provides state management for components that use the TagTable (primarily without a backing entity)
 * Allows setting of the schema tags and tag schema from the callbacks provided by the TagTable interface
 * and computes required field errors as tags are edited
 *
 * @returns
 * tagSchema: the tag schema object or null if no schema is selected
 * schemaTags: also known as Schema Field Values, an array of SchemaTag
 * errorsByFieldsId: an object containing indicators on if a field is required. Used for display on the TagTable
 * udpateTagSchema: setter to update the tagSchema, can be optionally called with an initial array of
 *                  field values (tags)
 * updateSchemaTag: Setter to update an individual tag based on an index and a new tag
 */
export function useTagTableState(
  initialTagSchema?: TagSchema | null,
  initialTags?: Array<SchemaTag>
): {
  tagSchema: TagSchema | null;
  schemaTags: Array<SchemaTag>;
  updateTagSchema: (newTagSchema: TagSchema | null, initialTags?: Array<SchemaTag>) => void;
  updateSchemaTag: (tagIndex: number, newValue: Tag) => void;
} {
  const [tagSchema, setTagSchema] = useState<TagSchema | null>(initialTagSchema || null);
  const [schemaTags, setSchemaTags] = useState<Array<SchemaTag>>(initialTags || []);

  const updateTagSchema = useCallback(
    (newTagSchema: TagSchema | null, initialTags?: Array<SchemaTag>): void => {
      setTagSchema(newTagSchema);
      if (newTagSchema != null) {
        if (initialTags) {
          setSchemaTags(initialTags);
        } else {
          setSchemaTags(newTagSchema.fields.map((field: SchemaField) => getInitialTag(field)));
        }
      } else {
        setSchemaTags([]);
      }
    },
    []
  );

  const updateSchemaTag = useCallback(
    (tagIndex: number, newValue: Tag): void => {
      const oldTag = schemaTags[tagIndex];
      const newTags = produce(schemaTags, (draftSchemaTags) => {
        draftSchemaTags[tagIndex] = {...oldTag, ...newValue};
      });
      setSchemaTags(newTags);
    },
    [schemaTags]
  );

  return {
    tagSchema,
    schemaTags,
    updateTagSchema,
    updateSchemaTag,
  };
}
